import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogActions } from "@material-ui/core";

import Button from "../../../../../../components/common/PL/Button/Button";
import EmpresaSearch from "./EmpresaSearch";
import RegistrarAutorizacionModal, {
  RegistrarAutorizacionSubmitData,
} from "./RegistrarAutorizacionModal";
import { useStore } from "../../../../../../hooks/useStore";
import { TransportOperationType } from "../../../../../../passapp-sdk/types/transportTypes";

import "./SolicitarAutorizacionModal.css";

type TipoOperacion = "Carga" | "Descarga";

const OBSERVACIONES_MAX_LENGTH = 1000;

export type AuthorizingCompany = { id: string; name: string };

export type SolicitarAutorizacionSubmitData = {
  company: string;
  operationType: TransportOperationType;
  /** Remito opcional; sólo aplica a una operación de carga. */
  receipt?: string;
  vehiclePlate: string;
  observations: string;
};

type SolicitarAutorizacionModalProps = {
  open: boolean;
  onClose: () => void;
  /** `approval` presente = el guardia ya consiguió la autorización por otro medio: la solicitud
   * nace aprobada (selfApprove) en lugar de quedar pendiente. */
  onSubmit?: (
    data: SolicitarAutorizacionSubmitData,
    approval?: RegistrarAutorizacionSubmitData
  ) => Promise<boolean>;
  prefillVehiclePlate?: string;
  /** Remito ya cargado en "Observaciones": se precarga acá para no pedirlo dos veces, pero —a
   * diferencia de la patente— sigue editable, porque acá es sólo un dato de conveniencia y no algo
   * que deba coincidir sí o sí con lo que se está registrando. */
  prefillRemito?: string;
  /** Empresa autorizante ya conocida (viene de la preautorización que originó el pedido de
   * dock): se preselecciona, pero el guardia puede cambiarla. */
  prefillCompany?: AuthorizingCompany | null;
};

const sanitizePatente = (value: string) =>
  value
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase()
    .slice(0, 20);

const sanitizeRemito = (value: string) => value.replace(/\D/g, "");

const SolicitarAutorizacionModal: React.FC<SolicitarAutorizacionModalProps> = ({
  open,
  onClose,
  onSubmit,
  prefillVehiclePlate,
  prefillRemito,
  prefillCompany,
}) => {
  const { authStore } = useStore();
  const companies: AuthorizingCompany[] = authStore.companies ?? [];

  const [empresaAutorizanteSearchValue, setEmpresaAutorizanteSearchValue] =
    React.useState<string>("");
  const [selectedEmpresaAutorizante, setSelectedEmpresaAutorizante] =
    React.useState<AuthorizingCompany | null>(null);
  const [tipoOperacion, setTipoOperacion] = React.useState<TipoOperacion | null>(null);
  const [remito, setRemito] = React.useState<string>("");
  const [vehiclePlate, setVehiclePlate] = React.useState<string>("");
  const [observaciones, setObservaciones] = React.useState<string>("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isRegistrarModalOpen, setIsRegistrarModalOpen] = React.useState(false);

  const isVehiclePlateLocked = !!sanitizePatente(prefillVehiclePlate ?? "");

  // La patente ya cargada en la sección de vehículo se prefillea y queda bloqueada acá para
  // que la autorización solicitada siempre corresponda al vehículo que se está registrando.
  React.useEffect(() => {
    if (!open) return;
    setVehiclePlate(sanitizePatente(prefillVehiclePlate ?? ""));
  }, [open, prefillVehiclePlate]);

  // El remito ya cargado en Observaciones se precarga acá (sin bloquear el campo, ver el tipo de
  // `prefillRemito`): antes sólo pasaba en el sentido modal -> Observaciones.
  React.useEffect(() => {
    if (!open) return;
    setRemito(sanitizeRemito(prefillRemito ?? ""));
  }, [open, prefillRemito]);

  // El tipo de operación nunca se prefillea (ni siquiera desde la preautorización que originó el
  // pedido): es una decisión explícita del guardia y arranca sin selección.
  React.useEffect(() => {
    if (!open) return;
    setTipoOperacion(null);
  }, [open]);

  // La empresa autorizante sí: si el pedido nace de una preautorización, ya sabemos a
  // quién pertenece, pero el guardia sigue pudiendo cambiarla.
  React.useEffect(() => {
    if (!open || !prefillCompany) return;
    setSelectedEmpresaAutorizante(prefillCompany);
    setEmpresaAutorizanteSearchValue(prefillCompany.name);
  }, [open, prefillCompany]);

  const vehiclePlateError =
    vehiclePlate.length > 0 && vehiclePlate.length < 6 ? "Debe tener entre 6 y 20 caracteres" : "";

  const isValid = !!selectedEmpresaAutorizante && !!tipoOperacion && vehiclePlate.length >= 6;

  const resetState = () => {
    setEmpresaAutorizanteSearchValue("");
    setSelectedEmpresaAutorizante(null);
    setTipoOperacion(null);
    setRemito("");
    setVehiclePlate("");
    setObservaciones("");
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetState();
    onClose();
  };

  const buildSubmitData = (
    empresa: AuthorizingCompany,
    operacion: TipoOperacion
  ): SolicitarAutorizacionSubmitData => ({
    company: empresa.id,
    operationType: operacion === "Descarga" ? "unload" : "load",
    ...(operacion === "Carga" && remito ? { receipt: remito } : {}),
    vehiclePlate,
    observations: observaciones.trim(),
  });

  const handleSubmit = async () => {
    if (!isValid || isSubmitting || !selectedEmpresaAutorizante || !tipoOperacion) return;
    setIsSubmitting(true);
    const success =
      (await onSubmit?.(buildSubmitData(selectedEmpresaAutorizante, tipoOperacion))) ?? true;
    setIsSubmitting(false);
    if (success) {
      resetState();
      onClose();
    }
  };

  // El guardia ya tiene la autorización de palabra: se manda la solicitud y la aprobación juntas.
  const handleRegistrarPorOtroMedio = async (
    approval: RegistrarAutorizacionSubmitData
  ): Promise<boolean> => {
    if (!isValid || !selectedEmpresaAutorizante || !tipoOperacion) return false;
    const success =
      (await onSubmit?.(buildSubmitData(selectedEmpresaAutorizante, tipoOperacion), approval)) ??
      true;
    if (success) {
      resetState();
      setIsRegistrarModalOpen(false);
      onClose();
    }
    return success;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ className: "solicitarAutorizacionModalCard" }}
      // maxWidth="xs"
      fullWidth
    >
      <DialogTitle disableTypography className="solicitarAutorizacionModalTitle">
        Solicitar autorización
      </DialogTitle>
      <DialogContent className="solicitarAutorizacionModalContent">
        <div className="solicitarAutorizacionModalSection">
          <span className="solicitarAutorizacionModalSectionLabel">Empresa autorizante</span>
          <EmpresaSearch
            searchValue={empresaAutorizanteSearchValue}
            onSearchChange={setEmpresaAutorizanteSearchValue}
            selectedEmpresa={selectedEmpresaAutorizante}
            onSelect={setSelectedEmpresaAutorizante}
            onClear={() => {
              setSelectedEmpresaAutorizante(null);
              setEmpresaAutorizanteSearchValue("");
            }}
            options={companies}
            getLabel={(empresa) => empresa.name}
            placeholder="-completar-"
            disabled={isSubmitting}
          />
        </div>

        <div className="solicitarAutorizacionModalSection">
          <span className="solicitarAutorizacionModalSectionLabel">Tipo de operación *</span>
          <div className="solicitarAutorizacionModalToggleRow">
            <button
              type="button"
              className={`solicitarAutorizacionModalToggleButton ${
                tipoOperacion === "Carga" ? "solicitarAutorizacionModalToggleButtonSelected" : ""
              }`}
              onClick={() => setTipoOperacion("Carga")}
              disabled={isSubmitting}
            >
              Carga
            </button>
            <button
              type="button"
              className={`solicitarAutorizacionModalToggleButton ${
                tipoOperacion === "Descarga" ? "solicitarAutorizacionModalToggleButtonSelected" : ""
              }`}
              onClick={() => setTipoOperacion("Descarga")}
              disabled={isSubmitting}
            >
              Descarga
            </button>
          </div>
        </div>

        {tipoOperacion === "Carga" && (
          <div className="solicitarAutorizacionModalSection">
            <span className="solicitarAutorizacionModalSectionLabel">Remito</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="solicitarAutorizacionModalTextInput"
              value={remito}
              onChange={(e) => setRemito(sanitizeRemito(e.target.value))}
              placeholder="-completar-"
              disabled={isSubmitting}
            />
          </div>
        )}

        <div className="solicitarAutorizacionModalSection">
          <span className="solicitarAutorizacionModalSectionLabel">Patente del vehículo</span>
          <input
            type="text"
            className="solicitarAutorizacionModalTextInput"
            value={vehiclePlate}
            onChange={(e) => setVehiclePlate(sanitizePatente(e.target.value))}
            placeholder="-completar-"
            disabled={isSubmitting || isVehiclePlateLocked}
          />
          {vehiclePlateError && (
            <span className="solicitarAutorizacionModalError">{vehiclePlateError}</span>
          )}
        </div>

        <div className="solicitarAutorizacionModalSection">
          <span className="solicitarAutorizacionModalSectionLabel">
            Observaciones para el autorizante
          </span>
          <textarea
            className="solicitarAutorizacionModalTextArea"
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value.slice(0, OBSERVACIONES_MAX_LENGTH))}
            placeholder="-completar-"
            rows={3}
            disabled={isSubmitting}
          />
          <span className="solicitarAutorizacionModalCounter">
            {observaciones.length}/{OBSERVACIONES_MAX_LENGTH}
          </span>
        </div>
      </DialogContent>
      <DialogActions className="solicitarAutorizacionModalActions">
        <Button
          variant="outline"
          onClick={handleClose}
          className="solicitarAutorizacionModalButton"
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          variant="neutral"
          onClick={handleSubmit}
          className="solicitarAutorizacionModalButton"
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
        >
          Solicitar autorización
        </Button>
      </DialogActions>

      <div className="solicitarAutorizacionModalSecondaryActions">
        <Button
          variant="outline"
          onClick={() => setIsRegistrarModalOpen(true)}
          className="solicitarAutorizacionModalButton transportRegistrarOtroMedioButton"
          disabled={!isValid || isSubmitting}
        >
          Registrar autorización por otro medio
        </Button>
      </div>

      <RegistrarAutorizacionModal
        open={isRegistrarModalOpen}
        onClose={() => setIsRegistrarModalOpen(false)}
        variant="entrada"
        prefillObservations={observaciones}
        onSubmit={handleRegistrarPorOtroMedio}
      />
    </Dialog>
  );
};

export default SolicitarAutorizacionModal;
